import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export type ButtonSwitcherOption = {
  value: string;
  label: string;
};

export type ButtonSwitcherProps = {
  options: ButtonSwitcherOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

export function ButtonSwitcher(props: ButtonSwitcherProps) {
  const { options, value, onChange, className } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });

  useEffect(
    function () {
      const container = containerRef.current;
      if (container == null) {
        return;
      }

      const activeButton = container.querySelector(`[data-value="${value}"]`) as HTMLButtonElement;

      if (activeButton != null) {
        const containerRect = container.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        setSliderStyle({
          left: buttonRect.left - containerRect.left,
          width: buttonRect.width,
        });
      }
    },
    [value, options]
  );

  return (
    <div
      ref={containerRef}
      className={twMerge(
        'relative inline-flex items-center rounded-full border border-branding-gray-200 bg-white p-1',
        className
      )}
    >
      <div
        className="absolute rounded-full bg-branding-dark-gray transition-all duration-300 ease-in-out"
        style={{
          left: `${sliderStyle.left}px`,
          width: `${sliderStyle.width}px`,
          height: 'calc(100% - 8px)',
          top: '4px',
        }}
      />
      {options.map(function (option) {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            data-value={option.value}
            onClick={function () {
              onChange(option.value);
            }}
            className={twMerge(
              'font-body-2-sb relative z-10 rounded-full px-6 py-2 transition-colors duration-200',
              isActive ? 'text-white' : 'text-branding-gray-600 hover:text-branding-gray-800'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
